import { PortalModule } from '@angular/cdk/portal';

import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { BrowserModule, Title } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { environment } from '@env/environment';
// Other modules
import { NbDialogModule, NbLayoutModule, NbThemeModule } from '@nebular/theme';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
// Ngx translate modules
import { TranslateLoader, TranslateModule, TranslateModuleConfig, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import * as Sentry from '@sentry/angular';
import { ServerTranslationLoader } from '@services/translations-loader/translations-loader.service';
import { NotifierModule, NotifierOptions } from 'angular-notifier';
import { RECAPTCHA_V3_SITE_KEY, RecaptchaV3Module } from 'ng-recaptcha';
import { QuillModule, QuillConfig } from 'ngx-quill';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AuthInterceptor } from './core/interceptors/auth.interceptor';
import { WINDOW_PROVIDER } from './core/providers/window.provider';
import { Router } from '@angular/router';
import { register } from 'swiper/element/bundle';
import { SharedModule } from './shared/shared.module';

register();

// Wyłącz funkcję pełnoekranową globalnie
window.addEventListener('load', () => {
  const swiperEl = document.querySelector('swiper-container');
  if (swiperEl) {
    Object.assign(swiperEl, {
      allowFullscreen: false
    });
  }
});

export function createTranslateLoader(http: HttpClient): ServerTranslationLoader {
  return new ServerTranslationLoader(http);
}

export function appInitializerFactory(translate: TranslateService): () => Promise<any> {
  const currentLanguage = localStorage.getItem('current_panel_language') ?? 'en';

  return () => {
    translate.setDefaultLang(currentLanguage);
    return translate.use(currentLanguage).toPromise();
  };
}

const notifierOptions: NotifierOptions = {
  position: {
    horizontal: {
      position: 'right',
      distance: 12
    },
    vertical: {
      position: 'top',
      distance: 12,
      gap: 10
    }
  },
  behaviour: {
    autoHide: 5000,
    onClick: false,
    onMouseover: 'pauseAutoHide',
    stacking: 3 // liczba widocznych notyfikacji w stosie
  },
  animations: {
    enabled: true,
    show: {
      preset: 'slide',
      speed: 300,
      easing: 'ease'
    },
    hide: {
      preset: 'fade',
      speed: 300,
      easing: 'ease',
      offset: 50
    },
    shift: {
      speed: 300,
      easing: 'ease'
    },
    overlap: 150 // nakładanie się notyfikacji (w px) ale nei działa chyba
  }
};

const translateConfig: TranslateModuleConfig = {
  loader: {
    provide: TranslateLoader,
    useFactory: createTranslateLoader,
    deps: [HttpClient]
  }
};

const quillConfig: QuillConfig = {
  modules: {
    keyboard: {
      bindings: {
        space: {
          key: 32,
          handler: function () {
            return true;
          }
        }
      }
    }
  }
};

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    HttpClientModule,
    TranslateModule.forRoot(translateConfig),
    NbDialogModule.forRoot(),
    NbThemeModule.forRoot({ name: 'default' }),
    NbLayoutModule,
    NotifierModule.withConfig(notifierOptions),
    BrowserAnimationsModule,
    RecaptchaV3Module,
    QuillModule.forRoot(quillConfig),
    PortalModule,
    SharedModule
  ],
  exports: [TranslateModule],
  providers: [
    NgbActiveModal,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true
    },
    Title,
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false // Set to false to disable the crash report modal
      })
    },
    {
      provide: Sentry.TraceService,
      deps: [Router]
    },
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializerFactory,
      deps: [TranslateService],
      multi: true
    },
    {
      provide: RECAPTCHA_V3_SITE_KEY,
      useValue: environment.recaptcha.siteKey
    },
    WINDOW_PROVIDER
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
  constructor(trace: Sentry.TraceService) {}
}

export function HttpLoaderFactory(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http);
}
